import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useMemo,
  useState,
} from 'react';

type ToastType = 'success' | 'info' | 'warning' | 'error';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

export interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(
  undefined
);

export const ToastProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType) => {
    const newToast = { id: message, message, type };
    setToasts((prevToasts) => [...prevToasts, newToast]);
  }, []);

  const handleClose = useCallback(
    (id: string) =>
      setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id)),
    []
  );

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts
        .filter(
          (toasts, i, arr) => arr.findIndex((t) => t.id === toasts.id) === i
        )
        .map((toast) => (
          <Snackbar
            key={toast.id}
            open
            autoHideDuration={5000}
            onClose={() => handleClose(toast.id)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={() => handleClose(toast.id)} severity={toast.type}>
              {toast.message}
            </Alert>
          </Snackbar>
        ))}
    </ToastContext.Provider>
  );
};
